import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import cx from "clsx"
import type { ServiceType } from "shared"
import { fetchServiceTypes, classifyServiceType } from "../api"
import { Checkbox } from "@rawa/ui/Checkbox"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./ServiceTypesListPage.module.scss"

type Classification = { serviceType: ServiceType; isContact: boolean }

export function ServiceTypesListPage() {
  const queryClient = useQueryClient()

  // Read each time the page is shown and never in the background; the write
  // below hands back what the server now holds.
  const {
    data: serviceTypes = [],
    isPending: loading,
    isError,
    error: loadFailure,
  } = useQuery({
    queryKey: ["service-types"],
    queryFn: ({ signal }) => fetchServiceTypes(signal),
    staleTime: Infinity,
    refetchOnMount: "always",
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchServiceTypes", forbiddenIsExpected: true },
  })
  const forbidden = isError && isForbidden(loadFailure)
  const loadError = isError && !forbidden

  const classification = useMutation({
    mutationFn: ({ serviceType, isContact }: Classification) =>
      classifyServiceType(serviceType.id, isContact),
    onError: (err) => trackError(err, { source: "classifyServiceType" }),
    onSuccess: (updated) =>
      queryClient.setQueryData<ServiceType[]>(["service-types"], (held) =>
        held?.map((s) => (s.id === updated.id ? updated : s)),
      ),
  })
  const savingId = classification.isPending
    ? classification.variables.serviceType.id
    : null

  const error = classification.isError
    ? `Couldn't update ${classification.variables.serviceType.name}. Please try again.`
    : null

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={cx(typography.h2, styles.heading)}>Service Types</h1>
          <p className={styles.subtitle}>
            Mark each ICIS service type as a contact or non-contact service.
          </p>
        </div>
      </div>

      {error && <ErrorCallout>{error}</ErrorCallout>}

      {!loading && forbidden && (
        <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>
      )}

      {loading && (
        <div
          role="status"
          aria-label="Loading service types"
          className={styles.panel}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.row}>
              <span className={styles.skeletonName} />
              <span className={styles.skeletonToggle} />
            </div>
          ))}
        </div>
      )}

      {!loading && loadError && (
        <ErrorCallout>Failed to load service types.</ErrorCallout>
      )}

      {!loading && !loadError && !forbidden && serviceTypes.length === 0 && (
        <p className={styles.empty}>No service types.</p>
      )}

      {!loading && !loadError && serviceTypes.length > 0 && (
        <div className={styles.panel}>
          <ul aria-label="Service types" className={styles.list}>
            {serviceTypes.map((st) => (
              <li key={st.id} className={styles.row}>
                <span className={styles.name}>{st.name}</span>
                <div className={styles.control}>
                  {savingId === st.id && (
                    <span role="status" className={styles.saving}>
                      Saving…
                    </span>
                  )}
                  <Checkbox
                    checked={st.isContact}
                    label="Contact"
                    disabled={savingId === st.id}
                    onChange={(isContact) =>
                      classification.mutate({ serviceType: st, isContact })
                    }
                    ariaLabel={`${st.name} is a contact service type`}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
